import api from '../../shared/services/api';

// Type definitions matching backend Pydantic schemas

export interface CorrelationCriterion {
  criterion: string;
  matched: boolean;
  value: any;
  weight: number;
  details: string;
}

export interface DocumentInfo {
  id: string;
  filename: string;
  type: string;
}

export interface DocumentPairCorrelation {
  doc_a: DocumentInfo;
  doc_b: DocumentInfo;
  correlated: boolean;
  confidence: number;
  relationship_type: string;
  matching_criteria: CorrelationCriterion[];
  non_matching_criteria: CorrelationCriterion[];
}

export interface UncorrelatedDocument {
  document_id: string;
  filename: string;
  document_type: string;
  reasons: string[];
}

export interface CorrelatedGroup {
  group_id: string;
  document_ids: string[];
  document_filenames: string[];
  relationship_type: string;
  confidence: number;
  document_count: number;
}

export interface DocumentSetCorrelationResponse {
  correlation_status: 'fully_correlated' | 'partially_correlated' | 'not_correlated';
  overall_confidence: number;
  document_count: number;
  correlated_pairs_count: number;
  total_pairs_count: number;
  pair_correlations: DocumentPairCorrelation[];
  uncorrelated_documents: UncorrelatedDocument[];
  correlated_groups: CorrelatedGroup[];
  validation_timestamp: string;
}

export interface DocumentRequirement {
  document_type: string;
  count: number;
  status: 'ok' | 'missing' | 'excess';
}

export interface DocumentRequirementsStatus {
  all_mandatory_present: boolean;
  missing_mandatory: string[];
  present_documents: DocumentRequirement[];
}

export interface FailedRule {
  rule: {
    primary_doc_type: string;
    required_doc_types: string[];
    correlation_criteria: string[];
    role: string;
  };
  document: string;
  missing_correlations: string[];
  error_message: string;
  reason: string;
}

export interface CorrelationRulesStatus {
  total_rules_checked: number;
  rules_passed: number;
  rules_failed: number;
  failed_rules: FailedRule[];
}

export interface ATLASCorrelationValidation {
  can_submit: boolean;
  atlas_ready: boolean;
  declaration_type: string;
  document_requirements: DocumentRequirementsStatus;
  correlation_rules: CorrelationRulesStatus;
  issues: string[];
  recommendations: string[];
  validation_timestamp: string;
}

export interface ComprehensiveCorrelationResponse {
  correlation_valid: boolean;
  confidence: number;
  document_count: number;
  correlated_groups: CorrelatedGroup[];
  uncorrelated_documents: UncorrelatedDocument[];
  correlation_details: DocumentSetCorrelationResponse;
  atlas_validation: ATLASCorrelationValidation;
}

export interface CorrelationValidationRequest {
  target_module: string;
  declaration_type: 'export' | 'import' | 'transit';
}

/**
 * Service for document correlation validation
 */
class CorrelationService {
  /**
   * Validate correlation for a document set
   * @param setId - Document set ID
   * @param declarationType - Type of declaration (export/import/transit)
   * @param targetModule - Target module (default: BEO_ATLAS)
   * @returns Comprehensive correlation validation results
   */
  async validateCorrelation(
    setId: string,
    declarationType: 'export' | 'import' | 'transit' = 'export',
    targetModule: string = 'BEO_ATLAS'
  ): Promise<ComprehensiveCorrelationResponse> {
    try {
      const request: CorrelationValidationRequest = {
        target_module: targetModule,
        declaration_type: declarationType,
      };

      const response = await api.post<ComprehensiveCorrelationResponse>(
        `/api/v1/document-sets/${setId}/validate-correlation`,
        request
      );

      return response.data;
    } catch (error: any) {
      console.error('Correlation validation error:', error);

      // Handle specific error cases
      if (error.response?.status === 400) {
        throw new Error(error.response.data?.detail || 'Invalid request for correlation validation');
      } else if (error.response?.status === 404) {
        throw new Error('Document set not found');
      } else if (error.response?.status === 422) {
        throw new Error('Validation error: ' + JSON.stringify(error.response.data?.detail || 'Invalid data'));
      }

      throw new Error(error.message || 'Failed to validate document correlation');
    }
  }

  /**
   * Get confidence level label based on score
   * @param confidence - Confidence score (0.0-1.0)
   * @returns Label and color
   */
  getConfidenceLabel(confidence: number): { label: string; color: string; severity: 'success' | 'warning' | 'error' } {
    if (confidence >= 0.75) {
      return { label: 'High Correlation', color: '#4CAF50', severity: 'success' };
    } else if (confidence >= 0.30) {
      return { label: 'Partial Correlation', color: '#FF9800', severity: 'warning' };
    } else {
      return { label: 'Low Correlation', color: '#F44336', severity: 'error' };
    }
  }

  /**
   * Get correlation status icon
   * @param status - Correlation status
   * @returns Icon name
   */
  getStatusIcon(status: 'fully_correlated' | 'partially_correlated' | 'not_correlated'): string {
    switch (status) {
      case 'fully_correlated':
        return 'CheckCircle';
      case 'partially_correlated':
        return 'Warning';
      case 'not_correlated':
        return 'Error';
      default:
        return 'Info';
    }
  }

  /**
   * Format criterion name for display
   * @param criterion - Criterion key
   * @returns Formatted name
   */
  formatCriterionName(criterion: string): string {
    const names: Record<string, string> = {
      reference_number: 'Reference Number',
      party_match: 'Party Matching',
      date_proximity: 'Date Proximity',
      product_match: 'Product Matching',
      transport_match: 'Transport Information',
      value_match: 'Value Comparison',
    };
    return names[criterion] || criterion;
  }

  /**
   * Get criterion icon
   * @param criterion - Criterion key
   * @returns Material-UI icon name
   */
  getCriterionIcon(criterion: string): string {
    const icons: Record<string, string> = {
      reference_number: 'Tag',
      party_match: 'People',
      date_proximity: 'CalendarToday',
      product_match: 'Inventory',
      transport_match: 'LocalShipping',
      value_match: 'AttachMoney',
    };
    return icons[criterion] || 'Info';
  }

  /**
   * Calculate percentage from confidence score
   * @param confidence - Confidence score (0.0-1.0)
   * @returns Percentage (0-100)
   */
  toPercentage(confidence: number): number {
    return Math.round(confidence * 100);
  }

  /**
   * Check if correlation is acceptable (>= 30%)
   * @param confidence - Confidence score
   * @returns True if acceptable
   */
  isAcceptableCorrelation(confidence: number): boolean {
    return confidence >= 0.30;
  }

  /**
   * Get recommendation priority
   * @param recommendation - Recommendation text
   * @returns Priority level
   */
  getRecommendationPriority(recommendation: string): 'high' | 'medium' | 'low' {
    const lowerRec = recommendation.toLowerCase();
    if (lowerRec.includes('missing') || lowerRec.includes('required') || lowerRec.includes('must')) {
      return 'high';
    } else if (lowerRec.includes('should') || lowerRec.includes('recommended')) {
      return 'medium';
    }
    return 'low';
  }

  /**
   * Get recommendation icon
   * @param recommendation - Recommendation text
   * @returns Material-UI icon name
   */
  getRecommendationIcon(recommendation: string): string {
    const lowerRec = recommendation.toLowerCase();
    if (lowerRec.includes('upload')) {
      return 'CloudUpload';
    } else if (lowerRec.includes('remove') || lowerRec.includes('delete')) {
      return 'Delete';
    } else if (lowerRec.includes('verify') || lowerRec.includes('check')) {
      return 'Verified';
    } else if (lowerRec.includes('correct') || lowerRec.includes('fix')) {
      return 'Build';
    }
    return 'Lightbulb';
  }

  /**
   * Group recommendations by category
   * @param recommendations - List of recommendations
   * @returns Grouped recommendations
   */
  groupRecommendations(recommendations: string[]): Record<string, string[]> {
    const groups: Record<string, string[]> = {
      upload: [],
      verify: [],
      fix: [],
      other: [],
    };

    recommendations.forEach((rec) => {
      const lowerRec = rec.toLowerCase();
      if (lowerRec.includes('upload')) {
        groups.upload.push(rec);
      } else if (lowerRec.includes('verify') || lowerRec.includes('check') || lowerRec.includes('ensure')) {
        groups.verify.push(rec);
      } else if (lowerRec.includes('correct') || lowerRec.includes('fix') || lowerRec.includes('update')) {
        groups.fix.push(rec);
      } else {
        groups.other.push(rec);
      }
    });

    return groups;
  }
}

export const correlationService = new CorrelationService();
export default correlationService;
